import React, { useState, useEffect, useRef } from 'react';
import webChatHistoryService, { EnhancedChatSession } from '../services/webChatHistoryService';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { PanelLeft } from 'lucide-react';
import UserMenu from './UserMenu';
import { useRISearch } from '../contexts/RISearchContext';

interface AppSidebarProps {
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  refreshTrigger?: number;
}

interface GroupedSessions {
  today: EnhancedChatSession[];
  yesterday: EnhancedChatSession[];
  lastWeek: EnhancedChatSession[];
  older: EnhancedChatSession[];
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  currentSessionId,
  onSessionSelect,
  onNewChat,
  isCollapsed,
  onToggleCollapse,
  refreshTrigger,
}) => {
  const { isSessionSearching } = useRISearch();

  // Sessions state
  const [risSessions, setRisSessions] = useState<EnhancedChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Load sessions on mount and when refreshTrigger changes
  useEffect(() => {
    loadAllSessions();
  }, []);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadAllSessions();
    }
  }, [refreshTrigger]);

  // Listen for session events to refresh sidebar
  useEffect(() => {
    const handleSessionCreated = () => {
      console.log('[AppSidebar] Session created, refreshing...');
      loadAllSessions();
    };

    const handleTitleUpdated = () => {
      console.log('[AppSidebar] Session title updated, refreshing...');
      loadAllSessions();
    };

    window.addEventListener('session:created', handleSessionCreated);
    window.addEventListener('session:titleUpdated', handleTitleUpdated);

    return () => {
      window.removeEventListener('session:created', handleSessionCreated);
      window.removeEventListener('session:titleUpdated', handleTitleUpdated);
    };
  }, []);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingSessionId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingSessionId]);

  const loadAllSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const risData = await webChatHistoryService.getSessionsByServiceType('ris', 50, 0);
      setRisSessions(risData);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Fehler beim Laden der Sessions');
    } finally {
      setLoading(false);
    }
  };

  const groupSessionsByDate = (sessions: EnhancedChatSession[]): GroupedSessions => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const groups: GroupedSessions = {
      today: [],
      yesterday: [],
      lastWeek: [],
      older: [],
    };

    sessions.forEach(session => {
      const sessionDate = new Date(session.lastMessageAt);
      if (sessionDate >= today) {
        groups.today.push(session);
      } else if (sessionDate >= yesterday) {
        groups.yesterday.push(session);
      } else if (sessionDate >= lastWeek) {
        groups.lastWeek.push(session);
      } else {
        groups.older.push(session);
      }
    });

    return groups;
  };

  const filterSessions = (sessions: EnhancedChatSession[]) => {
    if (!searchQuery.trim()) return sessions;
    const query = searchQuery.toLowerCase();
    return sessions.filter(
      session =>
        session.title.toLowerCase().includes(query) ||
        session.previewText?.toLowerCase().includes(query)
    );
  };

  const handleNewChat = () => {
    onNewChat();
  };

  const handleSessionSelect = (session: EnhancedChatSession) => {
    onSessionSelect(session.sessionId);
  };

  const startEditing = (session: EnhancedChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.sessionId);
    setEditingTitle(session.title);
  };

  const saveEdit = async () => {
    if (!editingSessionId || !editingTitle.trim()) return;

    try {
      await webChatHistoryService.updateSessionTitle(editingSessionId, editingTitle.trim());

      // Update local state
      setRisSessions(prev =>
        prev.map(s => (s.sessionId === editingSessionId ? { ...s, title: editingTitle.trim() } : s))
      );

      setEditingSessionId(null);
      setEditingTitle('');
    } catch (err) {
      console.error('Error updating session title:', err);
      setError('Fehler beim Speichern');
    }
  };

  const cancelEdit = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const deleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Sind Sie sicher, dass Sie diese Konversation löschen möchten?')) return;

    try {
      await webChatHistoryService.deactivateSession(sessionId);
      setRisSessions(prev => prev.filter(s => s.sessionId !== sessionId));
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Fehler beim Löschen');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    else if (e.key === 'Escape') cancelEdit();
  };

  const renderSessionItem = (session: EnhancedChatSession) => {
    const isActive = session.sessionId === currentSessionId;
    const isEditing = editingSessionId === session.sessionId;

    return (
      <div
        key={session.sessionId}
        className={`group relative mx-2 my-1 rounded-xl transition-colors ${
          isActive
            ? 'bg-white dark:bg-gray-700 border-l-[3px] border-l-accent border border-[rgba(30,77,107,0.1)] shadow-soft'
            : 'hover:bg-white dark:hover:bg-gray-700/50 border border-transparent'
        }`}
      >
        {isEditing ? (
          <div className="p-2 flex items-center gap-1">
            <input
              ref={editInputRef}
              type="text"
              value={editingTitle}
              onChange={e => setEditingTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-sm px-2 py-1 border border-[rgba(30,77,107,0.15)] dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-primary dark:text-white focus:ring-2 focus:ring-accent"
              onClick={e => e.stopPropagation()}
            />
            <button
              onClick={e => { e.stopPropagation(); saveEdit(); }}
              className="p-1 text-accent hover:bg-warm-100 rounded-lg"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); cancelEdit(); }}
              className="p-1 text-muted hover:bg-warm-100 rounded-lg"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleSessionSelect(session)}
            className="w-full text-left p-2"
          >
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <MagnifyingGlassIcon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-primary dark:text-white truncate">
                  {session.title}
                </div>
                {isSessionSearching(session.sessionId) ? (
                  <div className="flex items-center mt-1 text-xs text-accent">
                    <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
                    <span>Suche...</span>
                  </div>
                ) : (
                  <div className="flex items-center mt-1 text-xs text-muted">
                    <ClockIcon className="w-3 h-3 mr-1" />
                    {formatTime(session.lastMessageAt)}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}

        {/* Actions */}
        {!isEditing && (
          <div className="absolute right-1 top-1 hidden group-hover:flex gap-0.5">
            <button
              onClick={e => startEditing(session, e)}
              className="p-1 text-muted hover:text-primary hover:bg-warm-100 dark:hover:bg-gray-600 rounded-lg"
              title="Umbenennen"
            >
              <PencilIcon className="w-3 h-3" />
            </button>
            <button
              onClick={e => deleteSession(session.sessionId, e)}
              className="p-1 text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
              title="Löschen"
            >
              <TrashIcon className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderDateGroup = (label: string, sessions: EnhancedChatSession[]) => {
    if (sessions.length === 0) return null;
    return (
      <div key={label}>
        <div className="px-3 py-1 text-xs text-muted font-medium">{label}</div>
        {sessions.map(session => renderSessionItem(session))}
      </div>
    );
  };

  const renderSessionsList = () => {
    const filteredSessions = filterSessions(risSessions);
    const grouped = groupSessionsByDate(filteredSessions);

    if (filteredSessions.length === 0) {
      return (
        <div className="px-3 py-4 text-xs text-muted text-center">
          {searchQuery ? 'Keine Ergebnisse' : 'Noch keine Suchen'}
        </div>
      );
    }

    return (
      <>
        {renderDateGroup('Heute', grouped.today)}
        {renderDateGroup('Gestern', grouped.yesterday)}
        {renderDateGroup('Letzte 7 Tage', grouped.lastWeek)}
        {renderDateGroup('Älter', grouped.older)}
      </>
    );
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className={`${
        isCollapsed ? 'w-14' : 'w-72'
      } transition-all duration-300 overflow-hidden border-r border-[rgba(30,77,107,0.1)] dark:border-gray-700 glass dark:bg-gray-800 flex flex-col h-full relative`}
    >
      {/* Collapsed Icons */}
      <div
        className={`absolute inset-0 flex flex-col items-center py-3 transition-opacity duration-300 ${
          isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Sidebar erweitern"
        >
          <PanelLeft className="w-4 h-4 text-muted" />
        </button>
        <button
          onClick={handleNewChat}
          className="p-2 hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors mt-3"
          title="Neue Suche"
        >
          <PlusIcon className="w-4 h-4 text-muted" />
        </button>
        <div className="flex-1" />
        <div className="mb-2">
          <UserMenu isCollapsed={true} />
        </div>
      </div>

      {/* Expanded Content */}
      <div
        className={`flex flex-col h-full transition-opacity duration-300 ${
          isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {/* Header */}
        <div className="p-3 border-b border-[rgba(30,77,107,0.1)] dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-lg font-bold text-primary dark:text-white">RISearch</span>
          </div>
          <button
            onClick={onToggleCollapse}
            className="p-1.5 hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Sidebar einklappen"
          >
            <PanelLeft className="w-4 h-4 text-muted" />
          </button>
        </div>

        {/* New Chat Button */}
        <div className="p-2 border-b border-[rgba(30,77,107,0.1)] dark:border-gray-700">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="text-sm font-medium">Neue Suche</span>
          </button>
        </div>

        {/* Search */}
        <div className="p-2 border-b border-[rgba(30,77,107,0.1)] dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-muted" />
            <input
              type="text"
              placeholder="Verlauf durchsuchen..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-[rgba(30,77,107,0.15)] dark:border-gray-600 text-primary dark:text-white placeholder-muted rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-xs mt-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              Schließen
            </button>
          </div>
        )}

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-muted">
              <ArrowPathIcon className="w-5 h-5 animate-spin mx-auto mb-2 text-accent" />
              <span className="text-xs">Lade...</span>
            </div>
          ) : (
            <div className="py-2">
              {renderSessionsList()}
            </div>
          )}
        </div>

        {/* Footer with User Menu */}
        <div className="border-t border-[rgba(30,77,107,0.1)] dark:border-gray-700">
          <UserMenu isCollapsed={false} />
        </div>
      </div>
    </div>
  );
};

export default AppSidebar;
